import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import { StyledAudioVerticalResizeHandler } from "./styled";
import {
  blackAlpha008,
  blackAlpha24,
  lightInactive,
} from "../../_components/styled/variable";
import styled from "styled-components";
import { Rnd } from "react-rnd";
import { ENTER_ANIMATION, EXIT_ANIMATION } from "../../_actions/types";
import { STILL } from "../../_components/details/constants";
import { Tooltip } from "@chakra-ui/react";
import {
  FaBan,
  FaGripLinesVertical,
  FaSignInAlt,
  FaSignOutAlt,
} from "react-icons/fa";
import { t } from "i18next";

dayjs.extend(duration);

const StyledLayerBlock = styled(Rnd)`
  position: absolute;
  height: 100% !important;
  flex-shrink: 0;

  .block {
    height: 100%;
    width: 100%;
    background-color: ${blackAlpha008};
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    padding: 2px 14px;
    align-items: center;
  }

  .active {
    background-color: ${blackAlpha24};
  }
`;

export const StyledAnimationButton = styled.button`
  font-size: 14px;
  font-weight: 400;
  border: none;
  background-color: rgba(0, 0, 0, 0.24);
  color: ${lightInactive};
  padding: 4px;
  height: 24px;
  display: flex;
  width: 24px;
  border-radius: 8px;

  :active {
    outline: none;
    border: none;
  }

  :focus {
    outline: 0;
  }

  svg {
    width: 100% !important;
    height: 100%;
  }
`;

function LayerBlock({
  index,
  layer,
  isActive,
  onClick,
  scale,
  parentId,
  onResize,
  onResizeStop,
  onDrag,
  onDragStop,
  offset,
  width,
  minWidth,
  onClickAnimationOptions,
  enabled = true,
}) {
  const [isHovering, setIsHovering] = useState(false);

  const [hasEnterAnimation, hasExitAnimation] = useMemo(() => {
    const animations = layer?.payload?.animation;
    if (animations && animations.length) {
      let hasEnter = false;
      let hasExit = false;
      for (const a of animations) {
        if (a.motion === ENTER_ANIMATION && a.type !== STILL) {
          hasEnter = hasEnter || true;
        } else if (a.motion === EXIT_ANIMATION && a.type !== STILL) {
          hasExit = hasExit || true;
        }
      }
      return [hasEnter, hasExit];
    } else {
      return [false, false];
    }
  }, [layer?.payload?.animation]);

  return (
    <StyledLayerBlock
      bounds={parentId}
      $isActive={isActive}
      onClick={onClick}
      default={{
        x: offset,
        y: 0,
        width,
      }}
      size={{ width: width, height: "100%" }}
      position={{ x: offset, y: 0 }}
      onResize={onResize}
      onResizeStop={onResizeStop}
      onDrag={onDrag}
      onDragStop={onDragStop}
      minWidth={minWidth}
      disableDragging={!enabled}
      enableResizing={{
        top: false,
        bottom: false,
        left: enabled,
        right: enabled,
      }}
      resizeHandleStyles={{
        right: {
          right: "2px",
        },
        left: {
          left: "2px",
        },
      }}
      resizeHandleComponent={{
        right: (
          <Tooltip placement="top" hasArrow label={t("trim", { ns: "studio" })}>
            <StyledAudioVerticalResizeHandler>
              <FaGripLinesVertical />
            </StyledAudioVerticalResizeHandler>
          </Tooltip>
        ),
        left: (
          <Tooltip placement="top" hasArrow label={t("trim", { ns: "studio" })}>
            <StyledAudioVerticalResizeHandler>
              <FaGripLinesVertical />
            </StyledAudioVerticalResizeHandler>
          </Tooltip>
        ),
      }}
    >
      <div
        className={`block ${isActive || isHovering ? "active" : ""}`}
        onMouseEnter={(e) => {
          setIsHovering(true);
        }}
        onMouseLeave={(e) => {
          setIsHovering(false);
        }}
      >
        {isActive || isHovering ? (
          <>
            <StyledAnimationButton
              onClick={(e) => {
                onClickAnimationOptions();
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onMouseUp={(event) => {
                event.stopPropagation();
              }}
            >
              {hasEnterAnimation ? <FaSignInAlt /> : <FaBan />}
            </StyledAnimationButton>
            <StyledAnimationButton
              onClick={(e) => {
                onClickAnimationOptions();
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onMouseUp={(event) => {
                event.stopPropagation();
              }}
            >
              {hasExitAnimation ? <FaSignOutAlt /> : <FaBan />}
            </StyledAnimationButton>
          </>
        ) : (
          <>
            {hasEnterAnimation ? <FaSignInAlt /> : <div />}
            {hasExitAnimation ? <FaSignOutAlt /> : <div />}
          </>
        )}
      </div>
    </StyledLayerBlock>
  );
}

LayerBlock.defaultProps = {
  scale: 1,
};

LayerBlock.propTypes = {
  index: PropTypes.number.isRequired,
  parentId: PropTypes.string.isRequired,
  layer: PropTypes.object.isRequired,
  offset: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  minWidth: PropTypes.number,
  id: PropTypes.string,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
  scale: PropTypes.number,
  onResize: PropTypes.func.isRequired,
  onResizeStop: PropTypes.func.isRequired,
  onDrag: PropTypes.func.isRequired,
  onDragStop: PropTypes.func.isRequired,
  onClickAnimationOptions: PropTypes.func.isRequired,
};

export default LayerBlock;
